import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

export const BatchDemo = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], ["4%", "-4%"]);

  return (
    <section id="enterprise" ref={ref} className="py-24 md:py-32 lg:py-40 overflow-hidden" data-testid="enterprise-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: EASE }}
          className="lg:col-span-5"
        >
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5">05 — Enterprise batch</span>
          <h2 className="font-medium tracking-tight leading-tight text-3xl md:text-4xl lg:text-5xl mb-8 text-ink">
            Master your acquisitions.
          </h2>
          <p className="text-base text-fog leading-relaxed mb-12 max-w-md">
            Process thousands of files simultaneously for due diligence. The secure batch engine handles complex
            structural transformations so you can focus on the deal. What you see below is the actual workspace —
            the same sixteen engines you can try at the top of this page.
          </p>
          <div className="flex gap-12 font-mono text-[11px] uppercase tracking-[0.2em] text-mist">
            <span><span className="block text-2xl font-medium tracking-tight text-ink mb-1.5 font-sans">12,450</span>Files per batch</span>
            <span><span className="block text-2xl font-medium tracking-tight text-ink mb-1.5 font-sans">100K+</span>Monthly enterprise volume</span>
          </div>
        </motion.div>

        <motion.div
          style={{ y: cardY }}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <div className="bg-white border border-edge rounded-sm shadow-[0_20px_40px_-20px_rgba(0,0,0,0.08)] overflow-hidden" data-testid="batch-demo-card">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-hairline">
              <span className="w-2.5 h-2.5 rounded-full bg-black/10" />
              <span className="w-2.5 h-2.5 rounded-full bg-black/15" />
              <span className="w-2.5 h-2.5 rounded-full bg-black/20" />
              <span className="ml-3 font-mono text-[11px] text-mist">doclyx.com — the actual workspace</span>
            </div>
            <img
              src="/workspace-preview.png"
              alt="The Doclyx workspace: upload area, sixteen engine chips, compression target picker, and run button"
              className="block w-full h-auto"
              data-testid="workspace-preview-image"
              loading="lazy"
            />
            <div className="border-t border-hairline px-6 md:px-8 py-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
                A live screenshot of the product — no mockups, no staged demos
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
